import { isArray, isUndefined } from '@fxts/core';
import { html, on, rune } from 'rune-ts';
import klass from './LandingInput.module.scss';
import { InputBase } from '../../atoms/Input/InputBase';
import { InputTextProps } from '../../atoms/Input/type';
import {
  InputSideController,
  InputSideControllerEnable,
} from '../../atoms/Input/InputSide/InputSideController';
import { htmlIf } from '../../../../shared/util';
import { typo, typoPcMo } from '../../../../shared/typography/typo';
import { InputHelper } from '../../atoms/Input/InputHelper';
import { InputErrorIcon } from '../../atoms/Icon';
import { InputResetAfterEvent } from '../../atoms/Input/InputEvent';

export class LandingInputSideController extends InputSideController<InputTextProps> {
  errorIcon = InputErrorIcon;
  is_error = false;

  protected override template() {
    return html` <div class="${klass.controller}" data-error="${this.is_error}">
      ${this.resetButton}
      <div class="${klass.error_icon}">${this.errorIcon}</div>
    </div>`;
  }

  setError(is_error: boolean) {
    this.element().dataset.error = `${is_error}`;
    this.is_error = is_error;
  }
}

export class LandingInputHelper extends InputHelper {
  constructor(props, public inputSideView: LandingInputSideController) {
    super(props);
  }

  override template() {
    if (this.error && this.error.is_error) {
      return html`
        <div class="${klass.helper}">
          <span data-error="${this.error.is_error}" class="${typo('12_medium')} ${klass.helper_text}"
            >${this.error.message}</span
          >
        </div>
      `;
    }
    return html`<div class="${klass.helper}"></div> `;
  }

  setCustomError(is_error: boolean) {
    if (this.error) {
      this.is_error = is_error;
      this.error.is_error = is_error;
      this.redraw();
    }
  }

  override setError() {
    super.setError();
    this.inputSideView.setError(this.getIsError());
  }
}

export class LandingInput extends InputBase<
  InputTextProps,
  InputSideController<InputTextProps>,
  InputSideControllerEnable<InputTextProps>
> {
  placeholder: string;
  override inputSideView: LandingInputSideController;
  override inputHelperView: LandingInputHelper;

  constructor(data: InputTextProps) {
    const { placeholder, ...restData } = data;
    super(restData, {
      InputSide: LandingInputSideController,
      InputSideEnable: InputSideControllerEnable,
    });
    this.inputSideView = new LandingInputSideController(restData);
    this.inputHelperView = new LandingInputHelper(restData, this.inputSideView);
    this.inputView.className += klass.input;
    this.placeholder = placeholder || '';
  }

  override template() {
    const { custom_klass, error_helper, text_helper, name, required, readonly, disabled } = this.data;
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;

    const has_text_helper = !!text_helper && !error_helper;
    const has_error_helper = !isUndefined(error_helper) && !isArray(error_helper);
    const has_error_helpers = isArray(error_helper) && error_helper.length > 0;

    return html`
      <div
        name="${name}"
        data-error="${this.inputHelperView.getIsError()}"
        ${has_text_helper ? html`data-has_text_helper="${has_text_helper}"` : ''}
        ${has_error_helper ? html`data-has_error_helper="${has_error_helper}"` : ''}
        ${has_error_helpers ? html`data-has_error_helpers="${has_error_helpers}"` : ''}
        class="${klass.input_container} ${custom_klass || ''}"
      >
        <div class="wrapper ${readonly || disabled ? 'disabled' : ''} ${klass.input_wrapper}">
          <label class="${klass.input_label}">
            ${this.inputView}
            <div
              class="${klass.placeholder} ${typoPcMo({
                is_mobile,
                pc: '16_medium',
                mo: '14_medium',
              })}"
            >
              ${this.placeholder}
              ${htmlIf(
                html`
                  <span class="${klass.asterisk} ${typoPcMo({ is_mobile, pc: '14_medium', mo: '12_medium' })}"
                    >*</span
                  >
                `,
                !!required,
              )}
            </div>
          </label>
          ${this.inputSideView}
        </div>
        <div class="${klass.helper}">${this.inputHelperView}</div>
      </div>
    `;
  }

  getValue() {
    return this.data.value ?? '';
  }

  setValue(text: string) {
    this.data.value = text;
    this.inputView.setValue(text);
  }

  setCustomError(is_error: boolean) {
    this.inputHelperView.setCustomError(is_error);
    this.element().dataset.error = String(this.inputHelperView.getIsError());
    this.inputSideView.setError(this.inputHelperView.getIsError());
  }

  @on('input', 'input')
  @on(InputResetAfterEvent)
  placeholderShown() {
    const element = this.element();
    const input_element = this.element().querySelector(`input`);

    if (input_element && element) {
      if (input_element.value) {
        element.classList.add(klass.placeholder_hidden);
      } else {
        element.classList.remove(klass.placeholder_hidden);
      }
    }
  }

  protected override onRender() {
    super.onRender();
    this.placeholderShown();
  }
}
